import React, { useRef } from "react";
import { BandTemplate, LocalBandTemplate } from "../../../reducers/floorPlanExtraction";
import { PlanTypeExtension } from "../../../constants";

interface BandTemplatePanelProps {
  // 選択状態
  selectedBandTemplate: BandTemplate | null;
  localBandTemplate: LocalBandTemplate | null;
  // 帯設定
  bandPosition: 'top' | 'bottom';
  bandScale: number;
  // イベントハンドラ
  onShowTemplateSelectPopup: () => void;
  onPositionChange: (position: 'top' | 'bottom') => void;
  onScaleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  // ダウンロード関連
  sourceImageUrl: string | undefined;
  showDownloadDropdown: boolean;
  setShowDownloadDropdown: (show: boolean) => void;
  downloadDropdownRef: React.RefObject<HTMLDivElement>;
  // クレジット情報
  creditConsumed: number;
  creditLimit: number;
  isLimitInfinity: boolean;
  // プランタイプ
  planType: string | undefined;
  // キャンセル
  onCancel: () => void;
  // テンプレート登録数
  bandTemplatesCount: number;
  bandTemplatesLoading: boolean;
  // 新規登録フォーム表示
  onShowNewTemplateForm: () => void;
}

export const BandTemplatePanel: React.FC<BandTemplatePanelProps> = ({
  selectedBandTemplate,
  localBandTemplate,
  bandPosition,
  bandScale,
  onShowTemplateSelectPopup,
  onPositionChange,
  onScaleChange,
  sourceImageUrl,
  showDownloadDropdown,
  setShowDownloadDropdown,
  downloadDropdownRef,
  creditConsumed,
  creditLimit,
  isLimitInfinity,
  planType,
  onCancel,
  bandTemplatesCount,
  bandTemplatesLoading,
  onShowNewTemplateForm,
}) => {
  return (
    <div className="flex-shrink-0 bg-white rounded-lg p-3 flex flex-col text-xs overflow-y-auto" style={{ width: '220px', minWidth: '220px', maxWidth: '220px' }}>
      {/* テンプレート一覧ヘッダー */}
      <div className="mb-2">
        <h3 className="font-semibold text-gray-700">帯テンプレート</h3>
      </div>

      {/* テンプレート未登録時のヒント（テンプレートが1つでも登録されていれば非表示、ロード中も非表示） */}
      {!bandTemplatesLoading && !selectedBandTemplate && !localBandTemplate && bandTemplatesCount === 0 && (
        <div className="mb-2">
          <p className="text-xs leading-relaxed" style={{ color: '#ef4444' }}>
            帯テンプレートが未設定です。<br />
            新規登録ボタンからテンプレートを設定してください。
          </p>
          <button
            onClick={onShowNewTemplateForm}
            className="w-full mt-2 px-3 py-2 text-xs font-medium rounded-md bg-orange-500 text-white hover:bg-orange-600"
          >
            + 新規登録
          </button>
        </div>
      )}

      {/* テンプレート選択ボタン */}
      <button
        onClick={onShowTemplateSelectPopup}
        className="w-full flex items-center gap-2 p-2 rounded border border-gray-300 bg-white hover:bg-gray-50 mb-2 overflow-hidden"
      >
        {selectedBandTemplate ? (
          <>
            <div className="w-16 h-6 flex-shrink-0 bg-gray-200 rounded overflow-hidden">
              <img
                src={selectedBandTemplate.image_url}
                alt={selectedBandTemplate.name}
                className="w-full h-full object-contain"
              />
            </div>
            <div className="flex-1 min-w-0 text-left overflow-hidden">
              <div className="truncate font-medium text-sm">{selectedBandTemplate.name}</div>
              <div className="text-gray-400 text-[10px]">
                {selectedBandTemplate.position === 'top' ? '上部' : '下部'}
              </div>
            </div>
          </>
        ) : localBandTemplate ? (
          <>
            <div className="w-16 h-6 flex-shrink-0 bg-gray-200 rounded overflow-hidden">
              <img
                src={localBandTemplate.imageUrl}
                alt="一時的な帯"
                className="w-full h-full object-contain"
              />
            </div>
            <div className="flex-grow min-w-0 text-left">
              <div className="truncate font-medium text-sm text-blue-600">一時的な帯</div>
              <div className="text-blue-400 text-[10px]">未保存</div>
            </div>
          </>
        ) : (
          <span className="text-gray-500 text-sm flex-1 text-left">テンプレートを選択</span>
        )}
        <span className="text-gray-400 flex-shrink-0 ml-auto">▼</span>
      </button>

      {(selectedBandTemplate || localBandTemplate) && (
        <>
          <div className="border-t border-gray-200 my-2" />
          <h3 className="font-semibold mb-2 text-gray-700">帯の位置</h3>
          <div className="flex gap-1 mb-3">
            <button
              onClick={() => onPositionChange('top')}
              className={`flex-1 px-2 py-1 rounded ${
                bandPosition === 'top' ? 'bg-orange-500 text-white' : 'bg-gray-200'
              }`}
            >
              上部
            </button>
            <button
              onClick={() => onPositionChange('bottom')}
              className={`flex-1 px-2 py-1 rounded ${
                bandPosition === 'bottom' ? 'bg-orange-500 text-white' : 'bg-gray-200'
              }`}
            >
              下部
            </button>
          </div>

          <h3 className="font-semibold mb-2 text-gray-700">帯のサイズ</h3>
          <input
            type="range"
            min="0.5"
            max="1.5"
            step="0.1"
            value={bandScale}
            onChange={onScaleChange}
            className="w-full mb-1"
          />
          <div className="text-center text-gray-500 mb-3">{Math.round(bandScale * 100)}%</div>
        </>
      )}

      <div className="flex-grow" />

      <div className="space-y-2 mt-2">
        {/* ダウンロードドロップダウン */}
        <div ref={downloadDropdownRef}>
          <button
            onClick={() => sourceImageUrl && setShowDownloadDropdown(!showDownloadDropdown)}
            disabled={!sourceImageUrl}
            className={`w-full px-2 py-2 rounded font-medium relative flex items-center justify-center gap-2 ${
              sourceImageUrl
                ? 'submit-btn hover:submit-btn text-white cursor-pointer'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            <span>ダウンロード</span>
            {/* 消費クレジットバッジ（Mini/Liteプランのみ表示） */}
            {PlanTypeExtension.isMiniOrLite(planType) && (
              <span
                className="w-5 h-5 flex items-center justify-center text-xs rounded-full font-bold"
                style={{
                  backgroundColor: sourceImageUrl ? '#ffd2c1' : '#9ca3af',
                  color: sourceImageUrl ? '#ff4500' : '#e5e7eb',
                }}
              >
                1
              </span>
            )}
            <svg
              className={`w-4 h-4 transition-transform flex-shrink-0 absolute right-2 ${showDownloadDropdown ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
        <button
          onClick={onCancel}
          disabled={!sourceImageUrl}
          className={`w-full mt-2 px-3 py-2 text-sm font-medium rounded-md ${
            sourceImageUrl
              ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
          }`}
        >
          クリア
        </button>
      </div>
    </div>
  );
};

export default BandTemplatePanel;
